/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionLoopDeselect';

const ActionLoopDeselectManager: ActionManagerFn<'ActionLoopDeselect'> = (
  platform: Platform,
): ActionManager<'ActionLoopDeselect'> => {
  return {
    ...baseActionManager(platform, 'ActionLoopDeselect'),
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Deselect Record',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return ['ActionLoopRecords'].includes(parent.type);
    },
    icon: { iconName: 'square' },
    description: `**Overview**<br/>
The "Deselect Record" (Loop) action is a context-aware state management component designed specifically for use within an iteration cycle. Unlike the standard "Deselect Record" action which requires an explicit ID, this component automatically targets the current record being processed by the parent loop.<br/><br/>
This component is strictly purely contextual; it has no configuration properties because it derives all necessary information (which store and which record) directly from its parent ActionLoopRecords. It allows for dynamic "pruning" of selections during batch processing.

**Key Features**<br/>
* **Context-Driven Execution:** Automatically identifies and deselects the specific record currently held in the loop's iteration variable, eliminating the need for manual ID mapping.
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only as a child of ActionLoopRecords, ensuring it is never used in an invalid context where "current record" is undefined.
* **Zero Configuration:** Inherits all properties from baseActionManager with no additional inputs required.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Validation Cleanup:** Iterating through a list of "Selected" items and automatically deselecting any records that fail a validation check (e.g., "Deselect if Status is not 'Pending'").
* **Conditional Processing:** A user selects 50 items for approval; the loop checks each one, and if an item is missing required data, it deselects it to prevent it from being processed in the subsequent step.`,
  };
};

export default ActionLoopDeselectManager;
